import { z } from 'zod';
import type { MenuReader } from './enums';
import platforms from './platforms.json';

/*
 * Menus and dishes (skill antoja-lectura-de-cartas). The database has the last word
 * (migration 20261107000000): these schemas give the console the same answers sooner.
 */

/**
 * Platform pages are never read or copied: their terms forbid it. platforms.json is the
 * one list for the console and private.is_platform_url() (platforms.test.ts compares
 * them, and checks that it covers PLATFORM_DOMAINS in the pipeline).
 */
export const PLATFORMS = platforms;

const BRANDS = new RegExp(
  `(^|\\.)(${platforms.brands.map((brand) => brand.replaceAll('.', '\\.')).join('|')})\\.[a-z.]+$`,
);

// Mirrors private.guard_menu_url(): http(s) on the default ports, a real host, no user info.
const MENU_URL = /^https?:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(:(80|443))?([/?#]\S*)?$/i;

function hostOf(url: string): string | null {
  return /^[a-z]+:\/\/([^:/?#]+)/i.exec(url.trim())?.[1]?.toLowerCase() ?? null;
}

/** A brand with any suffix (rappi.com.pe) or a known domain and its subdomains. */
export function isPlatformUrl(url: string): boolean {
  const host = hostOf(url);
  return (
    host !== null &&
    (BRANDS.test(host) || platforms.domains.some((d) => host === d || host.endsWith(`.${d}`)))
  );
}

/** The business's own document on Google Drive, Docs or Sites: accepted, as a link only. */
export function isLinkOnlyUrl(url: string): boolean {
  const host = hostOf(url);
  return host !== null && platforms.linkOnlyHosts.includes(host);
}

/** A menu address. Its issues carry the hint codes the database raises. */
export const MenuUrlSchema = z
  .string()
  .trim()
  .refine((url) => url.length <= 500 && MENU_URL.test(url), { error: 'menu_url_invalid' })
  .refine((url) => isLinkOnlyUrl(url) || !isPlatformUrl(url), { error: 'menu_url_is_platform' });

/** COP, whole pesos. A price on request is no price (null), never 0. */
export const PriceSchema = z.number().int().min(1).max(9_999_999);

/** One dish of a reading (`menu_reads.result` is a list of these). */
export const MenuReadItemSchema = z.object({
  section: z
    .string()
    .trim()
    .max(80)
    .nullish()
    .transform((value) => value || null),
  name: z.string().trim().min(1).max(160),
  price: PriceSchema.nullish().transform((value) => value ?? null),
  tags: z.array(z.string()).default([]),
});
export type MenuReadItem = z.infer<typeof MenuReadItemSchema>;
export const MenuReadResultSchema = z.array(MenuReadItemSchema);

/** A dish as the editor saves it through public.save_menu_dishes(). */
export const DishInputSchema = z.object({
  id: z.uuid().nullable(),
  /** The dish's updated_at when it was loaded: the database refuses a stale edit. */
  updated_at: z.string().nullable(),
  section: z
    .string()
    .trim()
    .max(80)
    .nullable()
    .transform((value) => value || null),
  name: z.string().trim().min(1).max(160),
  price: PriceSchema.nullable(),
});
export type DishInput = z.infer<typeof DishInputSchema>;
export const DishInputListSchema = z.array(DishInputSchema);
/** Ids of the dishes the editor removed. */
export const DishIdListSchema = z.array(z.uuid());

// A whole-peso price: optional "$", thousands with ".", "," or a space, optional ",00".
const PRICE = /^\$?\s*(\d{1,3}(?:[.,\s]\d{3})+|\d+)(?:[.,]00)?$/;

/** "$28.000", "28,000", "28.000,00" or "28000" → 28000; empty → null; anything else → NaN. */
export function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const digits = PRICE.exec(trimmed)?.[1]?.replace(/[.,\s]/g, '');
  return digits ? Number(digits) : Number.NaN;
}

// A price at the end of a pasted line. It needs a "$" or at least 3 digits, so
// "Combo 2" stays a name.
const LINE_PRICE = /(\$\s*)?(\d{1,3}(?:[.,]\d{3})+|\d+)(?:[.,]00)?\s*$/;

/**
 * A menu pasted as text: one dish per line, its price at the end ("Soup ..... $28.000").
 * A line ending in a colon starts a section. A line without a price is a dish without one.
 */
export function parseMenuText(text: string): MenuReadItem[] {
  const items: MenuReadItem[] = [];
  let section: string | null = null;
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.endsWith(':')) {
      section = line.slice(0, -1).trim().slice(0, 80) || null;
      continue;
    }
    const found = LINE_PRICE.exec(line);
    const isPrice =
      found?.[2] !== undefined &&
      found.index > 0 &&
      (found[1] !== undefined || found[2].replace(/\D/g, '').length >= 3);
    const price = isPrice && found ? parsePrice(found[0]) : null;
    const name = (isPrice && found ? line.slice(0, found.index) : line).replace(
      /[\s\-–—:.·|]+$/,
      '',
    );
    if (!name) continue;
    items.push({ section, name: name.slice(0, 160), price: price || null, tags: [] });
  }
  return items;
}

export interface MenuReading {
  readonly reader: MenuReader;
  readonly createdAt: string;
  /** False when the stored result does not match MenuReadResultSchema: shown as unreadable. */
  readonly readable: boolean;
  readonly items: readonly MenuReadItem[];
}

/**
 * The first reading is the latest by Claude, OCR or the team; the second is the latest
 * by Gemini, made without seeing the first (skill antoja-lectura-de-cartas).
 */
export function pickReadings(reads: readonly MenuReading[]): {
  first: MenuReading | null;
  second: MenuReading | null;
} {
  const latest = (second: boolean) =>
    [...reads]
      .filter((read) => (read.reader === 'gemini') === second)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null;
  return { first: latest(false), second: latest(true) };
}

/** Lowercase, no accents, words only: "Crème Brûlée" and "creme brulee" match. */
export function normalizeDishName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Why a row needs a person: one reading lacks the dish, or the price or tags disagree. */
export type MenuRowDifference = 'missing' | 'price' | 'tags';

export interface MenuRow<D> {
  readonly key: string;
  readonly first: MenuReadItem | null;
  readonly second: MenuReadItem | null;
  readonly dish: D | null;
  /** Null while the second reading is pending: there is nothing to compare yet. */
  readonly difference: MenuRowDifference | null;
}

interface Named {
  readonly section: string | null;
  readonly name: string;
}

/** Section and name, plus how many times they came before: repeated dishes stay apart. */
function keysOf(items: readonly Named[]): string[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const base = `${normalizeDishName(item.section ?? '')}|${normalizeDishName(item.name)}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return `${base}#${count}`;
  });
}

function differenceOf(
  first: MenuReadItem | null,
  second: MenuReadItem | null,
): MenuRowDifference | null {
  if (!first || !second) return 'missing';
  if (first.price !== second.price) return 'price';
  // Tags count only when both readings bring them (a hand-typed reading has none).
  if (first.tags.length === 0 || second.tags.length === 0) return null;
  const tags = (item: MenuReadItem) => [...new Set(item.tags)].sort().join(',');
  return tags(first) === tags(second) ? null : 'tags';
}

/**
 * Lines up both readings and the saved dishes, dish by dish (same section and name).
 * Order: the first reading, then what only the second has, then dishes in neither.
 */
export function compareMenu<D extends Named>(
  first: readonly MenuReadItem[] | null,
  second: readonly MenuReadItem[] | null,
  dishes: readonly D[],
): MenuRow<D>[] {
  const rows = new Map<string, { first: MenuReadItem | null; second: MenuReadItem | null }>();
  const firstItems = first ?? [];
  const secondItems = second ?? [];
  keysOf(firstItems).forEach((key, index) => {
    rows.set(key, { first: firstItems[index] ?? null, second: null });
  });
  keysOf(secondItems).forEach((key, index) => {
    const item = secondItems[index] ?? null;
    const row = rows.get(key);
    if (row) row.second = item;
    else rows.set(key, { first: null, second: item });
  });
  const dishKeys = keysOf(dishes);
  const used = new Set<number>();
  const result: MenuRow<D>[] = [...rows].map(([key, row]) => {
    const index = dishKeys.indexOf(key);
    if (index >= 0) used.add(index);
    return {
      key,
      ...row,
      dish: index >= 0 ? (dishes[index] ?? null) : null,
      difference: second ? differenceOf(row.first, row.second) : null,
    };
  });
  dishes.forEach((dish, index) => {
    if (used.has(index)) return;
    result.push({
      key: `dish:${dishKeys[index] ?? index}`,
      first: null,
      second: null,
      dish,
      difference: null,
    });
  });
  return result;
}

/**
 * Where the editor starts: the saved dish. While the menu has no dishes yet, what the
 * readings say, except where they differ (or only one has the dish): those start empty,
 * to be confirmed by a person. Nothing is guessed.
 */
export function startingDish<D extends { readonly name: string; readonly price: number | null }>(
  row: MenuRow<D>,
  menuHasDishes: boolean,
): { name: string; price: number | null } {
  if (row.dish) return { name: row.dish.name, price: row.dish.price };
  const read = row.first ?? row.second;
  if (menuHasDishes || !read || row.difference !== null) return { name: '', price: null };
  return { name: read.name, price: read.price };
}
